import { createI18n } from 'vue-i18n'

import jaJP from '@/i18n/locales/ja-JP'
import zhCN from '@/i18n/locales/zh-CN'

/**
 * 支持的语言。
 *
 * 老板和员工可能是中国人也可能是日本人，同一个租户下两种人常常混在一起，
 * 所以语言是<b>每个账号各自的偏好</b>，不是租户级设置。
 */
export const SUPPORTED_LOCALES = [
  { value: 'zh-CN', label: '简体中文' },
  { value: 'ja-JP', label: '日本語' },
]

const STORAGE_KEY = 'cleanflow.locale'
const DEFAULT_LOCALE = 'zh-CN'

/**
 * 决定启动时用哪种语言。
 *
 * 优先级：用户上次选的 > 浏览器语言 > 默认中文。
 * 把用户的显式选择排在最前面，是因为浏览器语言经常不准——
 * 在日本买的手机系统是日文，但用的人可能是中国来的员工。
 *
 * @returns {string} 语言代码
 */
function resolveInitialLocale() {
  const saved = localStorage.getItem(STORAGE_KEY)
  if (saved && SUPPORTED_LOCALES.some((item) => item.value === saved)) {
    return saved
  }

  // navigator.language 的形态很杂：ja、ja-JP、zh、zh-CN、zh-Hans-CN 都可能出现，
  // 所以只取前两位做前缀匹配，不要求完全相等。
  const browserLang = (navigator.language || '').toLowerCase()
  if (browserLang.startsWith('ja')) {
    return 'ja-JP'
  }
  if (browserLang.startsWith('zh')) {
    return 'zh-CN'
  }

  return DEFAULT_LOCALE
}

export const i18n = createI18n({
  // 必须用 Composition API 模式（legacy: false），
  // 否则在 <script setup> 里拿不到 useI18n()。
  legacy: false,
  globalInjection: true,

  locale: resolveInitialLocale(),

  // 某个 key 在当前语言里缺失时回退到中文，界面上显示中文总好过显示原始 key。
  fallbackLocale: DEFAULT_LOCALE,

  messages: {
    'zh-CN': zhCN,
    'ja-JP': jaJP,
  },

  // 开发时把缺 key 的警告打开，生产关掉。
  //
  // 缺 key 不会报错，只会安静地回退成中文，所以日文界面里混进一句中文很容易
  // 一路带到线上都没人发现。控制台警告是开发期唯一会主动提醒的地方。
  // 生产环境关掉是因为用户的控制台里刷警告既没用又暴露内部 key 名。
  //
  // 提交前还可以跑 `node tools/check-i18n.mjs` 做一次全量比对，
  // 那个脚本连占位符对不对得上都会检查，比运行时警告更早发现问题。
  missingWarn: import.meta.env.DEV,
  fallbackWarn: import.meta.env.DEV,
})

/**
 * 切换界面语言。
 *
 * @param {string} locale 语言代码，必须在 {@link SUPPORTED_LOCALES} 之内
 */
export function setLocale(locale) {
  if (!SUPPORTED_LOCALES.some((item) => item.value === locale)) {
    return
  }

  i18n.global.locale.value = locale
  localStorage.setItem(STORAGE_KEY, locale)

  // 同步更新 <html lang>。这不只是为了语义正确：
  // 浏览器要靠它决定用中文字体还是日文字体来渲染汉字。不改的话，
  // 日文界面里的「直」「骨」这类字会用中文字形显示，日本用户一眼就看得出别扭。
  document.documentElement.setAttribute('lang', locale)
}

// 启动时同步一次，保证首屏字体就是对的。
document.documentElement.setAttribute('lang', i18n.global.locale.value)
